/**
 * NotificationHistoryModal — full notification history behind the bell's
 * "See all" button. The bell dropdown itself only ever shows page 1 of
 * unread notifications (client-side filtered), so there was previously no
 * way to get back to something already marked read or older than the
 * first page. This paginates every notification (read + unread) 20 at a
 * time via the same GET /notifications/ endpoint the bell uses, with
 * page/page_size params plus optional ?date=, ?is_read=, ?notification_type=,
 * and ?search= filters (apps/core/views.py NotificationViewSet.get_queryset).
 *
 * Props:
 *   open, onClose        — dialog visibility
 *   onNotificationRead   — (id) => void, so the bell's unread badge/list
 *                          stays in sync when a notification is read here
 *   onHistoryCleared     — () => void, called after "Clear history" so the
 *                          bell's own local list/badge empties out too
 *
 * "Clear history" here is deliberately a separate, confirmed, destructive
 * action from the bell dropdown's "Clear all" -- that button just marks
 * everything read (POST mark_all_read) and gets clicked often/casually;
 * this one permanently deletes every notification (POST clear_history) and
 * should not be one careless click away. It always clears everything, even
 * if a filter is currently narrowing the visible list -- the confirm dialog
 * says so explicitly to avoid "I only meant to clear today's" surprises.
 *
 * Clicking a row only closes the modal + navigates when the notification
 * actually has an action_url -- an informational notification just marks
 * itself read and the modal stays open, so triaging a long list doesn't
 * mean reopening it 15 times.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isToday, isYesterday, differenceInCalendarDays } from 'date-fns';
import { api } from '../../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { AlertTriangle, CalendarIcon, Loader2, Search, X } from 'lucide-react';
import NotificationRow from './NotificationRow';

const PAGE_SIZE = 20;
const READ_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
];
// Category filter options -- topical grouping the backend classifier
// (apps/core/notifications.py) writes to Notification.category on
// every save. Reads server-side via ?category= on
// GET /notifications/. Order matches the classifier's decision tree
// so the most common categories sit at the top.
const CATEGORY_FILTERS = [
  { value: 'all',             label: 'All categories' },
  { value: 'timesheet_info',  label: 'Timesheet info' },
  { value: 'exception',       label: 'Exceptions' },
  { value: 'action_required', label: 'Action required' },
  { value: 'reminder',        label: 'Reminders' },
  { value: 'requests',        label: 'Requests' },
  { value: 'calendar_events', label: 'Calendar events' },
  { value: 'other',           label: 'Other' },
];

// Today, as a 'yyyy-MM-dd' string in the browser's local calendar
// (matches how Notification.created_at renders in the row header).
// The modal opens filtered to today by default so the initial view
// isn't "everything you've ever received."
const todayYMD = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Buckets the current page's results into Today / Yesterday / This week /
 * Earlier sections -- purely presentational, applied to whatever page is
 * currently loaded (pagination itself stays server-side). */
function groupByDate(items) {
  const buckets = { Today: [], Yesterday: [], 'This week': [], Earlier: [] };
  items.forEach((n) => {
    const d = n.created_at ? new Date(n.created_at) : null;
    if (!d) { buckets.Earlier.push(n); return; }
    if (isToday(d)) buckets.Today.push(n);
    else if (isYesterday(d)) buckets.Yesterday.push(n);
    else if (differenceInCalendarDays(new Date(), d) <= 7) buckets['This week'].push(n);
    else buckets.Earlier.push(n);
  });
  return Object.entries(buckets).filter(([, group]) => group.length > 0);
}

export default function NotificationHistoryModal({ open, onClose, onNotificationRead, onHistoryCleared }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Filters -- dateFilter is a 'yyyy-MM-dd' string or null (matches the
  // app-wide ?date= convention, e.g. views_fmla_substitute.py). Default
  // is today so the modal opens with a manageable scope; user can click
  // the X to clear it and see everything.
  const [dateFilter, setDateFilter] = useState(todayYMD());
  const [readFilter, setReadFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const searchDebounce = useRef(null);

  const currentFilters = useCallback(() => ({
    date: dateFilter, readFilter, type: typeFilter, search: searchInput.trim(),
  }), [dateFilter, readFilter, typeFilter, searchInput]);

  const fetchPage = useCallback((pageNum, filters) => {
    const params = { page: pageNum, page_size: PAGE_SIZE };
    if (filters.date) params.date = filters.date;
    if (filters.readFilter === 'unread') params.is_read = 'false';
    if (filters.readFilter === 'read') params.is_read = 'true';
    if (filters.type && filters.type !== 'all') params.category = filters.type;
    if (filters.search) params.search = filters.search;

    setLoading(true);
    api.get('/notifications/', { params })
      .then((res) => {
        setResults(res.data.results || []);
        setCount(res.data.count ?? (res.data.results || []).length);
        setPage(pageNum);
      })
      .catch(() => { setResults([]); setCount(0); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) {
      const today = todayYMD();
      setDateFilter(today);
      setReadFilter('all');
      setTypeFilter('all');
      setSearchInput('');
      fetchPage(1, { date: today, readFilter: 'all', type: 'all', search: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced search -- refetch page 1 ~400ms after the user stops typing,
  // rather than firing a request per keystroke.
  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      fetchPage(1, currentFilters());
    }, 400);
    return () => clearTimeout(searchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleRowClick = (n) => {
    if (!n.is_read) {
      api.post(`/notifications/${n.id}/mark_read/`).catch(() => {});
      setResults((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      onNotificationRead?.(n.id);
    }
    // Only leave the modal for notifications that actually go somewhere --
    // an informational one just marks itself read and stays put, so
    // triaging a long list doesn't mean reopening the modal repeatedly.
    if (n.action_url) {
      onClose?.();
      navigate(n.action_url);
    }
  };

  const handleToggleRead = (n) => {
    const nextRead = !n.is_read;
    api.patch(`/notifications/${n.id}/`, {
      is_read: nextRead, read_at: nextRead ? new Date().toISOString() : null,
    }).catch(() => {});
    setResults((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: nextRead } : x)));
    if (nextRead) onNotificationRead?.(n.id);
  };

  const handleDeleteRow = (n) => {
    api.delete(`/notifications/${n.id}/`).catch(() => {});
    setResults((prev) => prev.filter((x) => x.id !== n.id));
    setCount((prev) => Math.max(0, prev - 1));
    if (!n.is_read) onNotificationRead?.(n.id);
  };

  const handleReadFilterChange = (value) => {
    setReadFilter(value);
    fetchPage(1, { ...currentFilters(), readFilter: value });
  };

  const handleTypeFilterChange = (e) => {
    const value = e.target.value;
    setTypeFilter(value);
    fetchPage(1, { ...currentFilters(), type: value });
  };

  const handleDateSelect = (d) => {
    const iso = d ? format(d, 'yyyy-MM-dd') : null;
    setDateFilter(iso);
    setDatePickerOpen(false);
    fetchPage(1, { ...currentFilters(), date: iso });
  };

  const clearDateFilter = () => {
    setDateFilter(null);
    fetchPage(1, { ...currentFilters(), date: null });
  };

  const handleClearHistory = () => {
    setClearing(true);
    api.post('/notifications/clear_history/')
      .then(() => {
        setResults([]);
        setCount(0);
        setPage(1);
        setDateFilter(null);
        setReadFilter('all');
        setTypeFilter('all');
        setSearchInput('');
        onHistoryCleared?.();
      })
      .catch(() => {})
      .finally(() => { setClearing(false); setConfirmClearOpen(false); });
  };

  const hasNext = page * PAGE_SIZE < count;
  const filtersActive = !!dateFilter || readFilter !== 'all' || typeFilter !== 'all' || !!searchInput.trim();
  const grouped = useMemo(() => groupByDate(results), [results]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-3xl" data-testid="notification-history-modal">
        <DialogHeader>
          <DialogTitle>All notifications</DialogTitle>
          <DialogDescription>
            {(() => {
              if (count === 0) return 'No notifications for the current filters.';
              const noun = count === 1 ? 'notification' : 'notifications';
              if (dateFilter) {
                const d = new Date(`${dateFilter}T00:00:00`);
                const label = isToday(d)
                  ? 'today'
                  : (isYesterday(d) ? 'yesterday' : format(d, 'MMM d, yyyy'));
                return `${count} ${noun} on ${label}`;
              }
              return `${count} ${noun}`;
            })()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pb-3 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
              {READ_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  data-testid={`notification-history-filter-${f.value}`}
                  className={cn(
                    'px-2.5 py-1 text-xs rounded-md transition-colors',
                    readFilter === f.value
                      ? 'bg-white text-slate-900 shadow-sm font-medium'
                      : 'text-slate-500 hover:text-slate-700',
                  )}
                  onClick={() => handleReadFilterChange(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <select
              value={typeFilter}
              onChange={handleTypeFilterChange}
              data-testid="notification-history-type-select"
              className="h-7 text-xs border border-slate-200 rounded-md px-2 text-slate-600 bg-white"
            >
              {CATEGORY_FILTERS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="notification-history-date-btn"
                  className={cn('h-7 gap-1.5 text-xs', dateFilter && 'border-blue-300 text-blue-700 bg-blue-50')}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {dateFilter ? format(new Date(`${dateFilter}T00:00:00`), 'MMM d, yyyy') : 'Filter by date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFilter ? new Date(`${dateFilter}T00:00:00`) : undefined}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {dateFilter && (
              <button
                type="button"
                aria-label="Clear date filter"
                data-testid="notification-history-date-clear-btn"
                className="text-slate-400 hover:text-slate-600"
                onClick={clearDateFilter}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search title or message…"
              data-testid="notification-history-search-input"
              className="w-full h-8 pl-8 pr-3 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>
        </div>

        <div className="h-[60vh] overflow-y-auto -mx-6 px-0 border-b border-slate-100">
          {loading ? (
            <p className="text-sm text-slate-500 flex items-center gap-2 p-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </p>
          ) : results.length > 0 ? (
            grouped.map(([label, items]) => (
              <div key={label}>
                <div className="px-6 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-50 sticky top-0 z-10">
                  {label}
                </div>
                {items.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onClick={handleRowClick}
                    onToggleRead={handleToggleRead}
                    onDelete={handleDeleteRow}
                  />
                ))}
              </div>
            ))
          ) : (
            <div className="p-10 text-center">
              <p className="text-sm italic text-gray-400">
                {filtersActive ? 'No notifications match these filters' : 'No notifications yet'}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 pb-2 px-1 border-t border-slate-100">
          {count > 0 ? (
            <button
              type="button"
              className="text-xs text-rose-600 hover:text-rose-700"
              data-testid="notification-history-clear-btn"
              onClick={() => setConfirmClearOpen(true)}
            >
              Clear history
            </button>
          ) : <span />}
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              {count > 0 ? `Page ${page} of ${Math.ceil(count / PAGE_SIZE)}` : ''}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1 || loading}
              data-testid="notification-history-prev-btn"
              onClick={() => fetchPage(page - 1, currentFilters())}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!hasNext || loading}
              data-testid="notification-history-next-btn"
              onClick={() => fetchPage(page + 1, currentFilters())}
            >
              Next
            </Button>
          </div>
        </div>
      </DialogContent>

      <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <AlertDialogContent data-testid="notification-history-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" /> Clear all notification history?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <span className="font-semibold">all</span> of your notifications, read and
              unread{filtersActive ? ' — including anything hidden by the current filters' : ''}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="notification-history-clear-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearHistory}
              disabled={clearing}
              data-testid="notification-history-clear-confirm-btn"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {clearing ? 'Clearing…' : 'Clear history'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
